const keys = [
  'notice',
  'counterAccount',
  'rate',
  'rateSource',
  'transferChoice',
  'createTransfer',
  'feeAmount',
  'chargebackSource',
  'principalAmount',
  'invoiceAmount',
  'moneyAmount',
  'pendingOUT',
  'pendingIN',
  'bankEvidence',
  'registered',
  'mappingMissing',
  'mappingInvalid',
  'transferRequired',
  'transferChanged',
  'transferMismatch',
  'rateRequired',
  'chargebackSourceRequired',
  'chargebackExceeded',
  'amountsMismatch',
  'reverseRelated',
  'sourceHint',
  'noSource',
  'newEvent',
  'eventKind',
  'ownAccount',
  'chargebackReverseFirst',
  'discountReopened',
] as const;
function labels(values: readonly string[]) {
  if (values.length !== keys.length) throw new Error('Native bank labels must cover all fields.');
  return Object.fromEntries(
    keys.map((key, index) => {
      const value = values[index];
      if (!value) throw new Error(`Missing native bank label ${key}`);
      return [key, value];
    }),
  );
}
export const bankLedgerI18n = {
  de: labels([
    'Prüfen Sie die tatsächliche Geldbewegung und Kontierung. Erst die Bestätigung bucht ins Hauptbuch; es wird keine Banküberweisung ausgelöst.',
    'Gegenkonto',
    'Tatsächlicher Kurs zur Buchhaltungswährung',
    'Quelle des tatsächlichen Kurses',
    'Übertrag zuordnen',
    'Bewusst einen neuen Übertrag erfassen',
    'Ausdrücklich enthaltene Bankgebühr',
    'Tatsächliche Ursprungszahlung',
    'Zurückbelasteter Zahlungsbetrag ohne Gebühr',
    'Wieder geöffneter Betrag in Belegwährung',
    'Anteil der zurückbelasteten Zahlung',
    'Abgangsnachweis fehlt',
    'Eingangsnachweis fehlt',
    'Banknachweis',
    'Bereits im Hauptbuch erfasst',
    'Ordnen Sie diesem Bankkonto zuerst ein aktives Geldkonto der Buchhaltung zu.',
    'Wählen Sie ein aktives, zur Buchungsart passendes Gegenkonto.',
    'Wählen Sie ausdrücklich einen bestehenden oder einen neuen eigenen Übertrag.',
    'Der Übertrag wurde verändert oder bereits anderweitig abgestimmt. Bitte erneut prüfen.',
    'Bankkonto, Datum, Währung, Betrag oder Gebühr stimmen nicht mit dem gewählten Übertrag überein.',
    'Für Fremdwährung sind der tatsächliche Kurs und seine Quelle erforderlich.',
    'Wählen Sie eine tatsächliche Ursprungszahlung mit noch rückbelastbaren Zuordnungen.',
    'Die Rückbelastung überschreitet die noch verfügbare Ursprungszahlung oder Zuordnung.',
    'Zahlungsbetrag, Gebühren und Zuordnungen ergeben nicht den tatsächlichen Bankbetrag.',
    'Eine Korrektur eines neu gebuchten Übertrags korrigiert zusammengehörige Bankseiten und ihre Buchungen gemeinsam. Das Lösen eines Nachweises für einen zuvor manuell gebuchten Übertrag erhält dessen Geldbuchungen.',
    'Wählen Sie den tatsächlich gemeinten Übertrag oder die Ursprungszahlung. Ein gleicher Betrag allein reicht nicht als Nachweis.',
    'Keine passende Ursprungszahlung vorhanden. Prüfen Sie zuerst die tatsächliche Zahlung und ihre Zuordnungen.',
    'Bankvorgang selbst zuordnen',
    'Art des Bankvorgangs',
    'Anderes eigenes Bankkonto',
    'Korrigieren Sie zuerst die verknüpften Rücklastschriften, bevor Sie die Ursprungszahlung oder deren Zuordnung korrigieren.',
    'Ursprünglicher Skonto wird anteilig zurückgenommen',
  ]),
  en: labels([
    'Review the actual cash movement and accounts. Confirmation posts to the ledger; it does not initiate a bank transfer.',
    'Counter account',
    'Actual rate to accounting currency',
    'Source of actual exchange rate',
    'Match transfer',
    'Explicitly record a new transfer',
    'Explicit included bank fee',
    'Actual original payment',
    'Charged-back principal excluding fee',
    'Reopened amount in invoice currency',
    'Share of charged-back payment',
    'Outgoing evidence missing',
    'Incoming evidence missing',
    'Bank evidence',
    'Already recorded in the ledger',
    'First map this bank account to an active ledger money account.',
    'Select an active counter account appropriate for this event.',
    'Explicitly choose an existing or new own-account transfer.',
    'The transfer changed or was reconciled elsewhere. Review it again.',
    'Bank account, date, currency, amount or fee does not match the selected transfer.',
    'A foreign-currency event requires the actual exchange rate and its source.',
    'Select an actual original payment with allocations still available for chargeback.',
    'The chargeback exceeds the remaining original payment or allocation.',
    'Principal, fees and allocations do not equal the actual bank movement.',
    'Correcting a newly posted transfer corrects related bank legs and journals together. Unlinking evidence from a transfer previously recorded manually retains its cash journals.',
    'Choose the actual transfer or original payment. Equal amounts alone are insufficient evidence.',
    'No suitable original payment is available. Review the actual payment and its allocations first.',
    'Classify bank event manually',
    'Bank event type',
    'Other own bank account',
    'Correct the linked chargebacks before correcting their original payment or allocation.',
    'Original cash discount is reversed proportionally',
  ]),
  tr: labels([
    'Gerçek para hareketini ve hesapları kontrol edin. Onay deftere kayıt yapar; banka havalesi başlatmaz.',
    'Karşı hesap',
    'Muhasebe para birimine gerçek kur',
    'Gerçek kurun kaynağı',
    'Aktarımı eşleştir',
    'Bilinçli olarak yeni aktarım kaydet',
    'Açıkça dahil edilen banka ücreti',
    'Gerçek kaynak ödeme',
    'Ücret hariç geri alınan ödeme tutarı',
    'Belge para biriminde yeniden açılan tutar',
    'Geri alınan ödemenin payı',
    'Çıkış kanıtı eksik',
    'Giriş kanıtı eksik',
    'Banka kanıtı',
    'Deftere zaten kaydedilmiş',
    'Önce bu banka hesabını etkin bir muhasebe para hesabına bağlayın.',
    'Bu olaya uygun etkin bir karşı hesap seçin.',
    'Mevcut veya yeni kendi hesapları arası aktarımı açıkça seçin.',
    'Aktarım değişti veya başka yerde mutabık hale getirildi. Tekrar inceleyin.',
    'Banka hesabı, tarih, para birimi, tutar veya ücret seçilen aktarımla uyuşmuyor.',
    'Yabancı para hareketi için gerçek kur ve kaynağı gereklidir.',
    'Geri alınabilecek eşleştirmeleri bulunan gerçek kaynak ödemeyi seçin.',
    'Geri alma, kaynak ödeme veya eşleştirmenin kalan tutarını aşıyor.',
    'Ana tutar, ücretler ve eşleştirmeler gerçek banka hareketine eşit değil.',
    'Yeni kaydedilen aktarımın düzeltilmesi ilişkili banka taraflarını ve yevmiyeleri birlikte düzeltir. Daha önce manuel kaydedilmiş aktarımın kanıt bağlantısını kaldırmak para yevmiyelerini korur.',
    'Gerçek aktarımı veya kaynak ödemeyi seçin. Yalnızca eşit tutarlar yeterli kanıt değildir.',
    'Uygun kaynak ödeme yok. Önce gerçek ödemeyi ve eşleştirmelerini inceleyin.',
    'Banka olayını manuel sınıflandır',
    'Banka olayı türü',
    'Diğer kendi banka hesabı',
    'İlk ödemeyi veya eşleştirmesini düzeltmeden önce ilişkili ters ibrazları düzeltin.',
    'İlk ödeme iskontosu aynı oranda geri alınır',
  ]),
  fr: labels([
    'Vérifiez le mouvement réel et les comptes. La confirmation comptabilise au grand livre sans déclencher de virement bancaire.',
    'Compte de contrepartie',
    'Taux réel vers la devise comptable',
    'Source du taux réel',
    'Rapprocher le virement',
    'Créer consciemment un nouveau virement',
    'Frais bancaires inclus explicitement',
    'Paiement d’origine réel',
    'Principal rejeté hors frais',
    'Montant rouvert en devise de facture',
    'Part du paiement rejeté',
    'Justificatif de sortie manquant',
    'Justificatif d’entrée manquant',
    'Justificatif bancaire',
    'Déjà comptabilisé au grand livre',
    'Rattachez d’abord ce compte bancaire à un compte de trésorerie comptable actif.',
    'Choisissez un compte de contrepartie actif adapté à cette opération.',
    'Choisissez explicitement un virement propre existant ou nouveau.',
    'Le virement a changé ou a été rapproché ailleurs. Vérifiez-le à nouveau.',
    'Compte bancaire, date, devise, montant ou frais ne correspondent pas au virement choisi.',
    'Une opération en devise exige le taux réel et sa source.',
    'Choisissez un paiement d’origine réel dont des affectations restent rejetables.',
    'Le rejet dépasse le solde du paiement ou de l’affectation d’origine.',
    'Principal, frais et affectations ne correspondent pas au mouvement bancaire réel.',
    'Corriger un nouveau virement comptabilisé corrige ensemble les côtés bancaires et les écritures liés. Dissocier le justificatif d’un virement déjà saisi manuellement conserve ses écritures de trésorerie.',
    'Choisissez le virement ou le paiement d’origine réel. Des montants égaux ne suffisent pas.',
    'Aucun paiement d’origine adapté. Vérifiez d’abord le paiement réel et ses affectations.',
    'Classer manuellement une opération bancaire',
    'Type d’opération bancaire',
    'Autre compte bancaire propre',
    'Corrigez les rejets liés avant de corriger leur paiement ou affectation d’origine.',
    'L’escompte initial est annulé proportionnellement',
  ]),
  es: labels([
    'Revise el movimiento real y las cuentas. La confirmación contabiliza en el mayor sin iniciar una transferencia bancaria.',
    'Contrapartida',
    'Tipo real a moneda contable',
    'Origen del tipo real',
    'Vincular traspaso',
    'Registrar conscientemente un traspaso nuevo',
    'Comisión bancaria incluida explícitamente',
    'Pago original real',
    'Principal devuelto sin comisión',
    'Importe reabierto en moneda de factura',
    'Parte del pago devuelto',
    'Falta justificante de salida',
    'Falta justificante de entrada',
    'Justificante bancario',
    'Ya registrado en el mayor',
    'Vincule primero esta cuenta bancaria a una cuenta contable de tesorería activa.',
    'Seleccione una contrapartida activa adecuada para esta operación.',
    'Elija explícitamente un traspaso propio existente o nuevo.',
    'El traspaso cambió o se concilió en otro lugar. Revíselo de nuevo.',
    'Cuenta bancaria, fecha, moneda, importe o comisión no coinciden con el traspaso elegido.',
    'Una operación en divisa requiere el tipo real y su origen.',
    'Seleccione un pago original real con asignaciones aún disponibles para devolución.',
    'La devolución supera el saldo del pago o de la asignación original.',
    'Principal, comisiones y asignaciones no coinciden con el movimiento bancario real.',
    'Corregir un traspaso nuevo contabilizado corrige conjuntamente sus partes bancarias y asientos. Desvincular el justificante de un traspaso registrado manualmente conserva sus asientos de tesorería.',
    'Elija el traspaso o pago original real. La igualdad de importes por sí sola no basta.',
    'No hay un pago original adecuado. Revise primero el pago real y sus asignaciones.',
    'Clasificar operación bancaria manualmente',
    'Tipo de operación bancaria',
    'Otra cuenta bancaria propia',
    'Corrija las devoluciones vinculadas antes de corregir el pago o su asignación original.',
    'El descuento original se revierte proporcionalmente',
  ]),
};
